import { fetchStaticDataV2 } from "@/game-tft/tft-fetch-static-data.mjs";
import { catchAllRoutes } from "@/routes/constants.mjs";
import type { BaseRoute } from "@/routes/routes.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import pathRegExp from "@/util/path-regexp.mjs";

export const tftChampTabs = ["overview", "items", "augments"].join("|");

const fetchLiveGameData = lazyFn(
  () => import("@/game-tft/tft-fetch-live-game-data.mjs"),
);
const fetchPostMatchData = lazyFn(
  () => import("@/game-tft/tft-fetch-postmatch-data.mjs"),
);
const fetchCoachingData = lazyFn(
  () => import("@/game-tft/tft-fetch-coaching-data.mjs"),
);
const fetchProBuildsData = lazyFn(
  () => import("@/game-tft/tft-fetch-probuilds-data.mjs"),
);
const tftFetchProfileData = lazyFn(
  () => import("@/game-tft/tft-fetch-profile-data.mjs"),
);
export const fetchTftData = lazyFn(
  () => import("@/game-tft/tft-fetch-static-data.mjs"),
);
export const fetchCompsData = lazyFn(
  () => import("@/game-tft/fetch-tft-comps-data.mjs"),
);
const fetchProfileData = lazyFn(
  () => import("@/game-tft/fetch-tft-profile-data.mjs"),
);
const fetchChampionData = lazyFn(
  () => import("@/game-tft/fetch-tft-champion-data.mjs"),
);
const fetchArticle = lazyFn(
  () => import("@/shared/fetch-contentful-article.mjs"),
);
const fetchUnitPowerupStats = lazyFn(
  () => import("@/game-tft/fetch-unit-powerup-stats.mjs"),
);
const fetchSet15Guides = lazyFn(
  () => import("@/game-tft/fetch-set-15-guides.mjs"),
);
const fetchUnitPowerupTiers = lazyFn(
  () => import("@/game-tft/fetch-unit-powerup-tiers.mjs"),
);

export const postMatchRoute = {
  path: pathRegExp("/tft/match/:region/:name/:matchId{/:tab}?"),
  component: "game-tft/PostMatch.jsx",
  async fetchData(...args) {
    await Promise.all([
      fetchPostMatchData(...args),
      fetchCoachingData(...args),
    ]);
  },
};

export const inGameRoute = {
  path: pathRegExp("/tft/in-game/:region/:name"),
  component: "game-tft/LiveGame.jsx",
  fetchData: fetchLiveGameData,
};

const routes: BaseRoute[] = [
  {
    path: pathRegExp("/tft/profile/:region/:name"),
    component: "game-tft/Profile.jsx",
    fetchData: tftFetchProfileData,
  },
  {
    path: pathRegExp("/tft/profile/:region/:name"),
    fetchData: fetchProfileData,
  },
  {
    path: pathRegExp("/tft/match/:region/:name/:matchId"),
    redirect: ({ parameters: [region, name, matchId] }) =>
      `/tft/match/${region}/${name}/${matchId}`,
  },
  postMatchRoute,
  {
    path: "/tft/probuilds",
    component: "game-tft/ProBuilds.jsx",
    fetchData: fetchProBuildsData,
  },
  {
    path: pathRegExp("/tft/:tab(champions|traits|items|augments)"),
    redirect: ({ parameters: [tab] }) => `/tft/${tab}/stats`,
  },
  {
    path: pathRegExp(
      "/tft/:tab(champions|traits|items|augments|legends)/:page(stats|overview)",
    ),
    component: "game-tft/StatsOverview.jsx",
  },
  {
    path: pathRegExp("/tft/tierlist{/:tab(champions|items|augments)}?"),
    component: "game-tft/TierList.jsx",
    async fetchData(_, searchParams) {
      await fetchTftData(searchParams);
    },
  },
  {
    path: /\/tft\/comps\/?(early)?$/,
    component: "game-tft/Comps.jsx",
    fetchData: fetchCompsData,
  },
  {
    path: pathRegExp(`/tft/champion/:champion{/:tab(${tftChampTabs})}?`),
    component: "game-tft/Champion.jsx",
    fetchData: fetchChampionData,
  },
  inGameRoute,
  {
    path: "/tft/guides",
    redirect: "/tft/guides/reroll",
  },
  {
    path: pathRegExp("/tft/guides/reroll"),
    component: "game-tft/GuideReRoll.jsx",
  },
  {
    path: pathRegExp("/tft/guides/loadeddice"),
    component: "game-tft/GuideLoadedDice.jsx",
  },
  {
    path: pathRegExp("/tft/guides/rounds"),
    component: "game-tft/GuideRounds.jsx",
  },
  {
    path: pathRegExp("/tft/guides/gold"),
    component: "game-tft/GuideExp.jsx",
  },
  {
    path: pathRegExp("/tft/guides/damage"),
    component: "game-tft/GuideDamage.jsx",
  },
  {
    path: pathRegExp("/tft/guides/tome-of-traits"),
    component: "game-tft/GuideTome.jsx",
  },
  {
    path: pathRegExp("/tft/article/:id"),
    component: "game-tft/Article.tsx",
    fetchData: fetchArticle,
  },

  // Patch notes/set releases
  // Its intended that this will continually grow, we want these to live on for SEO purposes
  {
    path: "/tft/updates",
    redirect: "/tft/set-15",
  },
  {
    path: /tft\/patchnotes(\?section=(systems|traits|champions|items|augments|sheet))?/,
    redirect: "/tft/set-13",
  },
  {
    path: "/tft/set",
    redirect: "/tft/set-13",
  },
  {
    path: "/tft/set-5_5",
    redirect: "/tft/set-5-revival",
  },
  {
    path: "/tft/set-4_5",
    redirect: "/tft/set-4-revival",
  },

  // Note to future maintainers:
  // Please preserve the old updates, it basically costs nothing to keep.
  // i.e. when there is set n+1, just create a new route/component for it,
  // leave the old one alone. Helps keep links werking.
  {
    path: "/tft/set-9-5",
    component: "game-tft/UpdatesSetNineHalf.jsx",
  },
  {
    path: "/tft/set-10",
    component: "game-tft/UpdatesSetTen.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set10");
    },
  },
  {
    path: "/tft/set-11",
    component: "game-tft/UpdatesSetEleven.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set11", { skipStats: true });
    },
  },
  {
    path: "/tft/set-3-5-revival",
    component: "game-tft/UpdatesSetThreeRevival.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set3_5");
    },
  },
  {
    path: "/tft/pengus-party",
    component: "game-tft/UpdatesFiveYear.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set11", { skipStats: true });
    },
  },
  {
    path: "/tft/set-12",
    component: "game-tft/UpdatesSetTwelve.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set12", { skipStats: true });
    },
  },
  {
    path: "/tft/set-5-revival",
    component: "game-tft/UpdatesSetFiveRevival.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set5_5", { skipStats: true });
    },
  },
  {
    path: "/tft/set-4-revival",
    component: "game-tft/UpdatesSetFourRevival.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set4_5", { skipStats: true });
    },
  },
  {
    path: "/tft/set-13",
    component: "game-tft/UpdatesSetThirteen.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set13", { skipStats: true });
    },
  },
  {
    path: "/tft/set-14",
    component: "game-tft/UpdatesSetFourteen.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set14", {
        skipStats: true,
        skipLocalization: true,
      });
    },
  },
  {
    path: "/tft/set-15",
    component: "game-tft/UpdatesSetFifteen.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set15", {
        skipStats: true,
        skipLocalization: true,
      });
    },
  },
  {
    path: "/tft/set-15/powerups",
    component: "game-tft/Set15Powerups.tsx",
    async fetchData(_, searchParams) {
      await Promise.all([
        fetchStaticDataV2(searchParams, "set15", {
          skipStats: true,
        }),
        fetchUnitPowerupStats(searchParams),
        fetchUnitPowerupTiers(searchParams),
      ]);
    },
  },
  {
    path: "/tft/set-15/crystal-gambit",
    component: "game-tft/Set15LootTableCrystalGambit.tsx",
    async fetchData(_, searchParams) {
      await Promise.all([
        fetchStaticDataV2(searchParams, "set15", {
          skipStats: true,
        }),
      ]);
    },
  },
  {
    path: "/tft/set-15/guides",
    component: "game-tft/Set15Guides.tsx",
    fetchData: fetchSet15Guides,
  },
  {
    path: "/tft/set-16",
    component: "game-tft/UpdatesSetSixteen.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set16", {
        skipStats: true,
        skipLocalization: true,
      });
    },
  },
  {
    path: "/tft/set-16/unlockable-units",
    component: "game-tft/Set16Unlockables.jsx",
    async fetchData(_, searchParams) {
      await fetchStaticDataV2(searchParams, "set16", {
        skipStats: true,
        skipLocalization: true,
      });
    },
  },
  {
    path: "/tft/set-16/ixtal",
    component: "game-tft/Set16LootTableIxtal.tsx",
    async fetchData(_, searchParams) {
      await Promise.all([
        fetchStaticDataV2(searchParams, "set16", {
          skipStats: true,
        }),
      ]);
    },
  },
];

routes.push({
  [catchAllRoutes]: routes,
  async fetchData(_, searchParams) {
    await Promise.all([fetchTftData(searchParams)]);
  },
});

export default routes;
